import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Ticket, User, UserPlus, Footprints, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useCustomer, customerApi, formatApiError } from "@/context/CustomerContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { API } from "@/lib/api";
import { getShopStatus, getPreviewToken } from "@/lib/shop";
import { normalizePhone, isValidGrPhone } from "@/lib/phone";
import { Seo } from "@/components/Seo";

export const OrderConfirmation = ({ order, onContinue }) => {
  const { t } = useLang();
  const s = t.shop;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="max-w-lg mx-auto text-center bg-white rounded-[2rem] border border-brand/10 shadow-[0_20px_60px_rgba(134,77,99,0.12)] p-10"
      data-testid="order-confirmation"
    >
      <span className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-bamboo-soft text-bamboo-deep mb-6">
        <CheckCircle2 className="h-10 w-10" />
      </span>
      <h1 className="font-heading text-4xl font-bold text-brand-ink">{s.confirm_title}</h1>
      <p className="mt-3 text-2xl font-mono font-bold text-brand" data-testid="order-number">{order.order_number}</p>
      <div className="mt-5 text-left rounded-2xl bg-brand-soft/40 border border-brand/10 p-4 space-y-1.5">
        {order.items.map((i, k) => (
          <div key={k} className="flex justify-between text-sm text-brand-ink/80">
            <span>{i.name_gr}{i.variant_label ? ` (${i.variant_label})` : ""} × {i.qty}</span>
            <span className="font-semibold">€{i.line_total.toFixed(2)}</span>
          </div>
        ))}
        {order.discount > 0 && (
          <div className="flex justify-between text-sm text-emerald-700">
            <span>{s.discount} {order.coupon_code}</span>
            <span className="font-semibold">-€{order.discount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-brand/10 font-bold text-brand-ink">
          <span>{s.total}</span>
          <span data-testid="order-total">€{order.total.toFixed(2)}</span>
        </div>
      </div>
      <p className="mt-4 text-sm text-brand-muted leading-relaxed">{s.confirm_body}</p>
      <p className="mt-2 text-xs text-brand-ink/50">{s.pickup_note}</p>
      <Button onClick={onContinue} className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold px-8 h-12" data-testid="confirm-back-shop">
        {s.back_shop}
      </Button>
    </motion.div>
  );
};

export default function CheckoutPage() {
  const { t, lang } = useLang();
  const s = t.shop;
  const a = t.account;
  const { items, subtotal, clear } = useCart();
  const { customer, ready, login, register } = useCustomer();
  const navigate = useNavigate();

  const [mode, setMode] = useState("guest"); // guest | login | register
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [authForm, setAuthForm] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "" });
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null); // {code, kind, amount, label}
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const discount = useMemo(() => {
    if (!applied) return 0;
    const d = applied.kind === "percent" ? subtotal * applied.amount / 100 : Math.min(subtotal, applied.amount);
    return Math.round(d * 100) / 100;
  }, [applied, subtotal]);
  const total = Math.round(Math.max(0, subtotal - discount) * 100) / 100;

  if (done) {
    return (
      <div className="min-h-screen bg-brand-soft/30">
        <Navbar />
        <main className="pt-32 pb-24 px-5">
          <OrderConfirmation order={done} onContinue={() => navigate("/shop")} />
        </main>
        <Footer />
      </div>
    );
  }

  const applyCoupon = async () => {
    const phone = customer ? customer.phone : form.phone;
    if (!isValidGrPhone(phone || "")) {
      toast.error(a.phone_hint);
      return;
    }
    try {
      const res = await api.get(`/coupons/validate?code=${encodeURIComponent(coupon)}&phone=${phone}&service=boutique`);
      if (res.data.valid) {
        setApplied({ code: coupon.trim().toUpperCase(), ...res.data });
        toast.success(`${res.data.label}`);
      } else {
        setApplied(null);
        toast.error(res.data.reason || s.coupon_invalid);
      }
    } catch {
      toast.error(s.coupon_invalid);
    }
  };

  const submit = async () => {
    const details = customer
      ? { first_name: customer.first_name, last_name: customer.last_name || "", email: customer.email, phone: customer.phone, notes: form.notes }
      : form;
    if (!details.first_name?.trim()) return toast.error(a.first_name);
    if (!details.email?.trim()) return toast.error(a.email);
    if (!isValidGrPhone(details.phone || "")) return toast.error(a.phone_hint);
    setBusy(true);
    try {
      const payload = {
        items: items.map((x) => ({ product_id: x.product_id, variant_id: x.variant_id, qty: x.qty })),
        first_name: details.first_name,
        last_name: details.last_name,
        email: details.email,
        phone: normalizePhone(details.phone),
        notes: details.notes || "",
        coupon_code: applied?.code || null,
      };
      const status = await getShopStatus();
      const headers = {};
      if (!status.enabled && getPreviewToken()) headers["X-Shop-Preview"] = getPreviewToken();
      const res = await customerApi.post("/shop/orders", payload, { headers });
      setDone(res.data);
      clear();
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doLogin = async () => {
    setBusy(true);
    try {
      await login(authForm.email, authForm.password);
      toast.success(a.title);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async () => {
    setBusy(true);
    try {
      await register(authForm);
      toast.success(a.title);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded-xl border-brand/20 bg-white h-11 focus-visible:ring-brand";

  return (
    <div className="min-h-screen bg-brand-soft/30">
      <Seo title={t.seo.checkout_title} path="/shop/checkout" noindex />
      <Navbar />
      <main className="pt-28 pb-24 px-5 sm:px-8 max-w-5xl mx-auto" data-testid="checkout-page">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-brand-ink">{s.checkout_title}</h1>

        {items.length === 0 ? (
          <div className="mt-12 text-center bg-white rounded-[2rem] border border-brand/10 p-12">
            <p className="text-brand-ink/50">{s.cart_empty}</p>
            <Button onClick={() => navigate("/shop")} className="mt-6 rounded-full bg-brand text-brand-cream font-bold" data-testid="checkout-back-shop">
              {s.back_shop}
            </Button>
          </div>
        ) : (
          <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_380px]">
            {/* Left: identity */}
            <div className="bg-white rounded-[2rem] border border-brand/10 p-6 sm:p-8">
              {ready && customer ? (
                <div className="flex items-center gap-4" data-testid="checkout-logged-in">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-brand-soft text-brand">
                    <User className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-bold text-brand-ink">{customer.first_name} {customer.last_name}</p>
                    <p className="text-sm text-brand-ink/60">{customer.email} · {customer.phone}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 flex-wrap" role="tablist">
                    {[
                      { id: "guest", label: s.mode_guest, icon: Footprints },
                      { id: "login", label: s.mode_login, icon: User },
                      { id: "register", label: s.mode_register, icon: UserPlus },
                    ].map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setMode(m.id)}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors no-a11y-underline ${
                          mode === m.id ? "bg-brand text-brand-cream" : "bg-brand-soft/50 text-brand-ink/70 hover:bg-brand-soft"
                        }`}
                        data-testid={`checkout-mode-${m.id}`}
                      >
                        <m.icon className="h-4 w-4" /> {m.label}
                      </button>
                    ))}
                  </div>

                  {mode === "guest" && (
                    <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="guest-form">
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.first_name}</Label>
                        <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="guest-first-name" />
                      </div>
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.last_name}</Label>
                        <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="guest-last-name" />
                      </div>
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.email}</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-1`} data-testid="guest-email" />
                      </div>
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.phone}</Label>
                        <Input
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: normalizePhone(e.target.value).slice(0, 10) })}
                          inputMode="tel"
                          maxLength={10}
                          placeholder="69XXXXXXXX"
                          className={`${inputCls} mt-1 ${form.phone && !isValidGrPhone(form.phone) ? "border-rose-400" : ""}`}
                          data-testid="guest-phone"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-brand-ink font-semibold">{s.notes}</Label>
                        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand/20 bg-white min-h-[70px] mt-1" data-testid="guest-notes" />
                      </div>
                    </div>
                  )}

                  {mode === "login" && (
                    <div className="mt-6 grid gap-4 max-w-sm" data-testid="login-form">
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.email}</Label>
                        <Input type="email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} className={`${inputCls} mt-1`} data-testid="login-email" />
                      </div>
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.password}</Label>
                        <Input type="password" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && doLogin()} className={`${inputCls} mt-1`} data-testid="login-password" />
                      </div>
                      <Button onClick={doLogin} disabled={busy} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11" data-testid="login-submit">
                        {busy ? a.logging_in : a.login_btn}
                      </Button>
                    </div>
                  )}

                  {mode === "register" && (
                    <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="register-form">
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.first_name}</Label>
                        <Input value={authForm.first_name} onChange={(e) => setAuthForm({ ...authForm, first_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="reg-first-name" />
                      </div>
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.last_name}</Label>
                        <Input value={authForm.last_name} onChange={(e) => setAuthForm({ ...authForm, last_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="reg-last-name" />
                      </div>
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.email}</Label>
                        <Input type="email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} className={`${inputCls} mt-1`} data-testid="reg-email" />
                      </div>
                      <div>
                        <Label className="text-brand-ink font-semibold">{a.phone}</Label>
                        <Input value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: normalizePhone(e.target.value).slice(0, 10) })} inputMode="tel" maxLength={10} placeholder="69XXXXXXXX" className={`${inputCls} mt-1`} data-testid="reg-phone" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-brand-ink font-semibold">{a.password}</Label>
                        <Input type="password" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} className={`${inputCls} mt-1`} data-testid="reg-password" />
                      </div>
                      <Button onClick={doRegister} disabled={busy} className="sm:col-span-2 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11" data-testid="reg-submit">
                        {busy ? a.registering : a.register_btn}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right: summary */}
            <div className="bg-white rounded-[2rem] border border-brand/10 p-6 sm:p-8 h-fit" data-testid="checkout-summary">
              <p className="font-heading text-2xl font-bold text-brand-ink flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-brand" /> {s.summary}
              </p>
              <div className="mt-4 space-y-3">
                {items.map((x) => (
                  <div key={`${x.product_id}-${x.variant_id}`} className="flex items-center gap-3 text-sm">
                    <div className="h-12 w-12 rounded-xl bg-stone-alt/50 overflow-hidden shrink-0">
                      {x.image && <img src={`${API}/${x.image}`} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-brand-ink truncate">{(lang === "en" && x.name_en) ? x.name_en : x.name_gr}</p>
                      <p className="text-xs text-brand-ink/50">{x.variant_label ? `${x.variant_label} · ` : ""}× {x.qty}</p>
                    </div>
                    <p className="font-bold text-brand-ink">€{(x.price * x.qty).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex gap-2">
                <Input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder={s.coupon_ph}
                  className="rounded-full border-brand/20 bg-brand-cream h-10 text-sm uppercase"
                  data-testid="coupon-input"
                />
                <Button onClick={applyCoupon} variant="outline" className="rounded-full border-brand/30 text-brand font-bold h-10" data-testid="coupon-apply-btn">
                  <Ticket className="h-4 w-4 mr-1" /> {s.coupon_apply}
                </Button>
              </div>
              {applied && (
                <p className="mt-2 text-xs font-bold text-emerald-700 flex items-center gap-1" data-testid="coupon-applied">
                  <Ticket className="h-3 w-3" /> {applied.code} · {applied.label}
                </p>
              )}

              <div className="mt-5 pt-4 border-t border-brand/10 space-y-1.5">
                <div className="flex justify-between text-sm text-brand-muted">
                  <span>{s.subtotal}</span>
                  <span data-testid="summary-subtotal">€{subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-emerald-700 font-semibold">
                    <span>{s.discount}</span>
                    <span data-testid="summary-discount">-€{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-heading text-2xl font-bold text-brand-ink pt-1">
                  <span>{s.total}</span>
                  <span data-testid="summary-total">€{total.toFixed(2)}</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-brand-ink/50 leading-relaxed">{s.pickup_note}</p>
              <Button onClick={submit} disabled={busy || (!customer && mode !== "guest")} className="mt-5 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base" data-testid="place-order-btn">
                {busy ? s.placing : s.place_order}
              </Button>
              {!customer && mode !== "guest" && (
                <p className="mt-2 text-xs text-center text-brand-ink/50">{s.mode_login} / {s.mode_register} ↑</p>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
